import React, { useCallback, useMemo, useState } from 'react';
import { AlertTriangle, Lock, LockOpen } from 'lucide-react';
import { reportsAPI } from '../../../services/api';
import { useToast, useConfirm } from '../../UIProvider';
import useReport from '../shared/useReport';
import KpiStrip from '../shared/KpiStrip';
import ReportTable from '../shared/ReportTable';
import PrintButton from '../shared/PrintButton';
import { clinicToday, formatClinicDate, formatClinicDateTime } from '../../../utils/clinicTime';
import { formatRs } from '../../../utils/money';
import { MonthPicker, monthKey, monthLabel } from '../../attendance/RegisterGrid';

// What each person is owed for the month, worked out from attendance: the monthly salary less a
// day's pay for every day not worked and not covered by paid leave, a holiday or a weekly off.
//
// Locking a month stores the figures as they stood when it was paid. After that the report serves
// the stored sheet rather than recalculating, so a correction made to attendance next week cannot
// quietly change what was already handed over.

const rs = (v) => (v == null ? '-' : formatRs(v));

const COLUMNS = [
  {
    key: 'userName', label: 'Name', wrap: true,
    render: (r) => (
      <span>
        {r.userName}
        {r.salaryMissing && (
          <span className="block text-xs text-amber-700">No salary set</span>
        )}
      </span>
    ),
  },
  { key: 'role', label: 'Role' },
  { key: 'salary', label: 'Salary', align: 'right', render: (r) => rs(r.salary), csv: (r) => r.salary },
  { key: 'workingDays', label: 'Working days', align: 'right' },
  { key: 'present', label: 'Present', align: 'right' },
  { key: 'halfDays', label: 'Half days', align: 'right' },
  { key: 'paidLeaveDays', label: 'Paid leave', align: 'right' },
  { key: 'absent', label: 'Absent', align: 'right' },
  { key: 'unpaidLeaveDays', label: 'Unpaid leave', align: 'right' },
  {
    key: 'deductedDays', label: 'Days deducted', align: 'right',
    render: (r) => <span className={Number(r.deductedDays) > 0 ? 'text-amber-700 font-medium' : ''}>{r.deductedDays}</span>,
  },
  { key: 'deduction', label: 'Deduction', align: 'right', render: (r) => rs(r.deduction), csv: (r) => r.deduction },
  {
    key: 'netPayable', label: 'Net payable', align: 'right',
    render: (r) => <span className="font-semibold text-gray-900">{rs(r.netPayable)}</span>,
    csv: (r) => r.netPayable,
  },
];

const PayrollReport = () => {
  const notify = useToast();
  const confirm = useConfirm();
  const [month, setMonth] = useState(() => monthKey(clinicToday()));
  const [busy, setBusy] = useState(false);

  const fetcher = useCallback(() => reportsAPI.getPayroll(month), [month]);
  const { data, loading, reload } = useReport(fetcher);

  const rows = useMemo(() => data?.rows || [], [data]);
  const totals = useMemo(() => {
    const sum = (f) => rows.reduce((n, r) => n + (Number(f(r)) || 0), 0);
    return {
      salary: sum((r) => r.salary),
      deduction: sum((r) => r.deduction),
      payable: sum((r) => r.netPayable),
      missing: rows.filter((r) => r.salaryMissing).length,
    };
  }, [rows]);

  const locked = data?.locked === true;
  const kpis = [
    { label: 'Staff', value: rows.length, sub: 'People on this month\'s payroll' },
    { label: 'Total salary', value: rs(totals.salary), sub: 'Before deductions' },
    { label: 'Deductions', value: rs(totals.deduction), sub: 'Days not worked and not covered', tone: totals.deduction > 0 ? 'alert' : undefined },
    { label: 'Net payable', value: rs(totals.payable), sub: 'What goes out this month' },
    { label: 'Salary not set', value: totals.missing, sub: 'Nothing can be worked out for them', tone: totals.missing > 0 ? 'alert' : undefined },
  ];

  const lock = async () => {
    const ok = await confirm({
      title: `Lock ${monthLabel(month)}?`,
      message: 'The figures are stored as they stand now. Later corrections to attendance or leave will not change this month again, which is what you want once the salaries have gone out.',
      confirmText: 'Lock the month',
      tone: 'primary',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await reportsAPI.lockPayroll({ month, force: month === monthKey(clinicToday()) });
      notify('success', `${monthLabel(month)} locked`);
      reload();
    } catch (e) {
      notify('error', e?.message || 'The month could not be locked.');
    } finally {
      setBusy(false);
    }
  };

  const unlock = async () => {
    const ok = await confirm({
      title: `Unlock ${monthLabel(month)}?`,
      message: 'The stored sheet is thrown away and the month is worked out again from attendance as it stands today. The figures may differ from what was paid.',
      confirmText: 'Unlock',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await reportsAPI.unlockPayroll(month);
      notify('success', `${monthLabel(month)} unlocked`);
      reload();
    } catch (e) {
      notify('error', e?.message || 'The month could not be unlocked.');
    } finally {
      setBusy(false);
    }
  };

  const denominator = data?.denominator === 'FIXED_30'
    ? 'a flat 30 days'
    : `the ${data?.denominatorDays || ''} days of the month`;

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl shadow p-4 flex items-center justify-between gap-3 flex-wrap">
        <MonthPicker month={month} onMonth={setMonth} max={monthKey(clinicToday())} />
        <div className="flex items-center gap-2 print:hidden">
          {locked ? (
            <button type="button" onClick={unlock} disabled={busy} className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              <LockOpen className="w-4 h-4" aria-hidden="true" /> Unlock
            </button>
          ) : (
            <button type="button" onClick={lock} disabled={busy || loading || rows.length === 0} className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
              <Lock className="w-4 h-4" aria-hidden="true" /> Lock the month
            </button>
          )}
          <PrintButton />
        </div>
      </div>

      {locked && (
        <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 rounded-lg text-sm text-emerald-900 flex items-start gap-2">
          <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <span>
            Locked by {data.lockedByName} on {formatClinicDateTime(data.lockedAt)}. These are the figures as they stood then,
            not a fresh calculation.
          </span>
        </div>
      )}

      {!locked && data?.throughDate && data.throughDate < `${month}-28` && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <span>
            This month is still running. Counted up to {formatClinicDate(data.throughDate)}; days after that are not treated as absent.
          </span>
        </div>
      )}

      <KpiStrip items={kpis} loading={loading} />

      <ReportTable
        title={`Payroll for ${monthLabel(month)}`}
        columns={COLUMNS}
        rows={rows}
        csvName={`payroll-${month}`}
        emptyText="Nobody is on the payroll for this month"
        footnote={`A day's pay is the salary divided by ${denominator}. Deducted days are absences, unpaid leave and half of each half day. Paid leave, holidays and weekly offs are paid in full. A punch waiting to be reviewed counts as present until it is rejected.`}
        pageSize={25}
      />
    </div>
  );
};

export default PayrollReport;
